import { useCallback, useMemo } from 'react';
import type { Audience, Production } from '@/api/types';
import type { CampaignAudienceSlot } from '@/lib/campaign-audience-slots';
import { AudienceBuilder, type AudiencePreviewState } from './audience';

export function equalAudienceAllocations(count: number): number[] {
  if (count <= 0) return [];
  const base = Math.floor(10_000 / count);
  const remainder = 10_000 - (base * count);
  return Array.from({ length: count }, (_, index) =>
    base + (index < remainder ? 1 : 0));
}

// A lookalike audience launches on CTV and Display now: the TTD prospect lane uploads the
// modelled people's UID2 tokens in the background after saving, and launch simply waits for
// the audience to finish syncing — the same launch-when-ready wait every audience has.
//
// This note used to be a red "cannot launch" warning from before that lane existed. It stays
// as an expectation-setter rather than disappearing entirely, because a prospect audience's
// first sync genuinely takes longer than a customer audience's (fit + token minting), and an
// operator armed with that knowledge stops refreshing the launch button.
export function campaignLookalikeLaunchWarning(channel: string | null | undefined): string | null {
  if (channel !== 'ctv' && channel !== 'dsp') return null;
  const channelLabel = channel === 'ctv' ? 'CTV' : 'Display';
  return `A lookalike uploads its modelled prospects to The Trade Desk in the background after saving, so a ${channelLabel} campaign built on one launches once that sync completes.`;
}

export interface CampaignAudienceChannelNoticeProps {
  channel?: string | null;
  /** Single-type callers, including the existing guard tests. */
  audienceType?: string;
  /** Every type in the campaign. Any lookalike delays launch, not only an
   *  all-lookalike campaign. */
  audienceTypes?: readonly string[];
}

export function CampaignAudienceChannelNotice({ channel, audienceType, audienceTypes }: CampaignAudienceChannelNoticeProps) {
  const warning = campaignLookalikeLaunchWarning(channel);
  if (!warning) return null;
  // Any lookalike in the campaign delays launch, not only a campaign that is
  // entirely lookalike. `audienceType` stays supported so the existing tests
  // and any single-type caller keep working unchanged.
  const selected = audienceTypes
    ? audienceTypes.includes('Lookalike')
    : audienceType === 'Lookalike';
  if (!selected) return null;

  return (
    <div
      className="facet"
      role="note"
      style={{
        marginBottom: 12,
        borderColor: 'var(--line-strong)',
        background: 'var(--bg-sunk)',
      }}
    >
      <div style={{display:'flex',flexDirection:'column',gap:4}}>
        <strong style={{fontFamily:'var(--font-display)',fontSize:12.5,textTransform:'uppercase'}}>
          Lookalike audiences sync before launch
        </strong>
        <span style={{fontSize:11.5,color:'var(--ink-2)',lineHeight:1.45}}>
          {warning} Arm the launch now and the campaign starts itself when the audience is ready.
        </span>
      </div>
    </div>
  );
}

/** What one card may change on its slot. */
export type CampaignAudienceSlotPatch = Partial<Pick<CampaignAudienceSlot, 'type' | 'groups'>>;

export interface CampaignAudienceSlotCardProps {
  slot: CampaignAudienceSlot;
  /** 0-based position of the slot in the campaign. */
  index: number;
  total: number;
  /** This slot's share of the budget, in basis points. */
  allocationBps: number;
  /** The campaign's production. Only its id is read: the builder is handed the API row with
   *  that id from `productions`, whatever view shape the wizard holds. */
  production: Pick<Production, 'id'> | null;
  audiences: readonly Audience[];
  productions: readonly Production[];
  /** The campaign has left draft: the audience type can no longer change. */
  locked: boolean;
  onPatch: (slotId: string, patch: CampaignAudienceSlotPatch) => void;
  /** Absent when the slot cannot be removed (the last one, or a published campaign). */
  onRemove?: (() => void) | null;
  onPreview: (slotId: string, preview: AudiencePreviewState) => void;
  confirmed: boolean;
  onConfirm: () => void;
  onEdit: () => void;
}

/**
 * One campaign audience: its own type, its own rules, its own size.
 *
 * Each card is one `AudienceBuilder`, which is why the builder itself needed no
 * change — `RULE_FIELDS[type]`, `onTypeChange` resetting groups, `loadSaved`
 * retyping and `canAddAudienceGroup` are all correct once "the builder" means
 * "one audience" rather than "the whole campaign".
 */
export function CampaignAudienceSlotCard({
  slot, index, total, allocationBps, production, audiences, productions,
  locked, onPatch, onRemove, onPreview, confirmed, onConfirm, onEdit,
}: CampaignAudienceSlotCardProps) {
  const handlePreview = useCallback(
    (next: AudiencePreviewState) => onPreview(slot.id, next),
    [onPreview, slot.id],
  );
  // The wizard holds its production in the legacy view shape (`title`, a venue name),
  // but the builder lists and labels productions by the API row (`name`, `venue_id`).
  // Handed the legacy object, it put the campaign's own production first in every
  // production picker labelled with its raw id. Hand it the row that object came from.
  const productionRow = useMemo(
    () => (production ? productions.find((row) => row.id === production.id) ?? null : null),
    [production, productions],
  );
  return (
    <div className="facet" style={{marginBottom:12,padding:'12px 14px'}}>
      <div className="facet-hd" style={{marginBottom:10}}>
        <h5>Audience {index + 1} of {total}</h5>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span className="tag">{slot.type}</span>
          <span className="muted mono" style={{fontSize:10.5}}>
            {(allocationBps / 100).toFixed(allocationBps % 100 === 0 ? 0 : 2)}% of budget
          </span>
          {onRemove && (
            <button type="button" className="btn-ghost" style={{fontSize:11}} onClick={onRemove}>
              Remove
            </button>
          )}
        </div>
      </div>
      <AudienceBuilder
        production={productionRow}
        confirmed={confirmed}
        audiences={audiences}
        productions={productions}
        type={slot.type}
        setType={(next) => onPatch(slot.id, { type: next })}
        groups={slot.groups}
        setGroups={(next) => onPatch(slot.id, { groups: next })}
        typeLocked={locked}
        lockHint={!locked}
        onPreviewChange={handlePreview}
        onConfirm={onConfirm}
        onEdit={onEdit}
      />
    </div>
  );
}
